import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import PlayQueueActions from 'reducers/PlayQueue'
import ModalActions from 'reducers/Modal'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'
import { useTheme } from 'styled-components'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { MaterialIcon } from '@dsny/dsny-component-library'
import { thunks as DownloadThunks } from 'reducers/Downloads'
import PlaylistActions, { thunks as PlaylistThunks } from 'reducers/PlayList'
import NotificationActions from 'reducers/Notification'
import styles from '../Dropdowns.module.scss'
import { AudioFormat, NotificationType } from 'constants/enums'
import { trackEvent } from 'utils/segmentService'
import { trackQueueEvent } from 'utils/analytics'

const TrackActionsDropdown = ({ track = {} }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isMobile = useMobileMediaQuery()
  const theme = useTheme()
  const { playlistAbstracts } = useSelector((state) => state.playList)

  const handlePlayNext = (e) => {
    e.stopPropagation()
    trackQueueEvent('Queue Play Next', track, {
      source: 'release_information',
      type: 'queue'
    })
    dispatch(PlayQueueActions.insertItemNext(track))
  }

  const handlePlayLater = (e) => {
    e.stopPropagation()
    trackQueueEvent('Queue Play Later', track, {
      source: 'release_information',
      type: 'queue'
    })
    dispatch(PlayQueueActions.insertItemAtBottom(track))
  }

  const handleDownloadSong = async (e, song, format) => {
    e.stopPropagation()
    if (song.assettype === 'video') {
      dispatch(
        DownloadThunks.addVideoToDownloadQueue(
          song,
          'release_information_track_actions_dropdown'
        )
      )
    } else {
      dispatch(
        DownloadThunks.addSongToDownloadQueue(song, {
          format,
          source: 'release_information_track_actions_dropdown'
        })
      )
    }
  }

  const openNewPlaylistModal = (e) => {
    e.stopPropagation()
    dispatch(PlaylistActions.setSongsToAdd([track.trackid]))
    dispatch(ModalActions.openPlaylistModal('track_actions'))
  }

  const handleAddToPlaylist = (e, playlist, trackItem) => {
    e.stopPropagation()
    dispatch(
      PlaylistThunks.addTracks(playlist.playlistid, [trackItem.trackid], () => {
        dispatch(
          NotificationActions.broadcastMessage(
            {
              songTitle: trackItem.title,
              playlistTitle: playlist.name
            },
            NotificationType.addedToPlaylist
          )
        )
        trackEvent('Release Add To Playlist', {
          release_id: trackItem?.releaseid || trackItem?.release_id || '',
          artist: trackItem?.artist || '',
          title: trackItem?.title || '',
          playlist_id: playlist?.playlistid,
          playlist_name: playlist?.name,
          track_count: 1,
          source: 'release_information',
          type: 'playlist'
        })
      })
    )
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        style={{ justifySelf: 'center' }}
        onClick={(e) => e.stopPropagation()}
      >
        <MaterialIcon name="MoreVertical" color={theme.colors.neutralW100} />
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side="bottom"
          align="end"
          style={{
            background: theme.colors.neutralW20,
            color: theme.colors.neutralW100
          }}
          className={styles.DropdownMenuContent}
          sideOffset={5}
          avoidCollisions
          collisionPadding={150}
        >
          {track.candownload && (
            <>
              {' '}
              <DropdownMenu.Item
                className={styles.DropdownMenuItem}
                onClick={(e) =>
                  handleDownloadSong(e, track, AudioFormat.wav?.value)
                }
              >
                {t('general.download-wav')}
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className={styles.DropdownMenuItem}
                onClick={(e) =>
                  handleDownloadSong(e, track, AudioFormat.mp3?.value)
                }
              >
                {t('general.download-mp3')}
              </DropdownMenu.Item>
            </>
          )}
          {!isMobile && (
            <DropdownMenu.Sub>
              <DropdownMenu.SubTrigger
                className={styles.DropdownMenuSubTrigger}
              >
                {t('playlist.add-to-playlist')}
                <div className={styles.RightSlot}>
                  <MaterialIcon
                    name="PlayArrow"
                    color={theme.colors.neutralW100}
                  />
                </div>
              </DropdownMenu.SubTrigger>
              <DropdownMenu.Portal>
                <DropdownMenu.SubContent
                  style={{
                    background: theme.colors.neutralW20,
                    color: theme.colors.neutralW100
                  }}
                  className={styles.DropdownMenuSubContent}
                  sideOffset={2}
                  alignOffset={-5}
                  avoidCollisions
                  collisionPadding={150}
                >
                  {playlistAbstracts?.map((playlist) => {
                    return (
                      <DropdownMenu.Item
                        key={playlist?.playlistid}
                        className={styles.DropdownMenuItem}
                        onClick={(e) => handleAddToPlaylist(e, playlist, track)}
                      >
                        {playlist?.name}
                      </DropdownMenu.Item>
                    )
                  })}

                  <DropdownMenu.Item
                    className={styles.DropdownMenuItem}
                    onClick={openNewPlaylistModal}
                  >
                    {t('playlist.new-playlist')}{' '}
                    <div className={styles.RightSlot}>
                      <MaterialIcon name="Add" />
                    </div>
                  </DropdownMenu.Item>
                </DropdownMenu.SubContent>
              </DropdownMenu.Portal>
            </DropdownMenu.Sub>
          )}

          <DropdownMenu.Arrow
            className={styles.DropdownMenuArrow}
            style={{ fill: theme.colors.neutralW20 }}
          />

          <DropdownMenu.Item
            className={styles.DropdownMenuItem}
            onClick={handlePlayNext}
          >
            {t('general.play-next')}
          </DropdownMenu.Item>

          <DropdownMenu.Item
            className={styles.DropdownMenuItem}
            onClick={handlePlayLater}
          >
            {t('general.play-later')}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

export default TrackActionsDropdown
